import { Fragment, useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { listarOrdenes, filtrarOrdenes, limpiarOrdenes } from '../../../actions/ordenesDevolucion';

import EliminarOrdenDevolucionModal from './modals/EliminarOrdenDevolucionModal';

const opcionesStatus = [
    { value: 1, label: 'Creada' },
    { value: 2, label: 'Completada' },
    { value: 3, label: 'Cancelada' },
    { value: 4, label: 'Eliminada' }
];

const formatDate = (value) => {
    if (!value) return 'Sin definir';
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString('es-MX');
};

const renderBadgeStatus = (status) => {
    const s = Number(status);
    if (s === 1) return <span className="badge bg-info text-dark">Creada</span>;
    if (s === 2) return <span className="badge bg-success">Completada</span>;
    if (s === 3) return <span className="badge bg-warning text-dark">Cancelada</span>;
    if (s === 4) return <span className="badge bg-secondary">Eliminada</span>;
    return <span className="badge bg-secondary">Sin definir</span>;
};

const OrdenesDevolucionProyecto = ({ idProyecto }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { filtrado } = useSelector(state => state.ordenesDevolucion);

    const [textoFiltro, setTextoFiltro] = useState('');
    const [statusSel, setStatusSel] = useState(null);
    const [ordenEliminar, setOrdenEliminar] = useState(null);

    useEffect(() => {
        if (!idProyecto) return;

        dispatch(listarOrdenes({ id_proyecto: idProyecto }));

        return () => {
            dispatch(limpiarOrdenes());
        };
    }, [dispatch, idProyecto]);

    const onChangeFiltroTabla = (e) => {
        const val = e.target.value;
        setTextoFiltro(val);
        dispatch(filtrarOrdenes(val));
    };

    const onChangeStatus = (opcion) => {
        setStatusSel(opcion);
        dispatch(listarOrdenes({ id_proyecto: idProyecto, status: opcion?.value ?? null }));
    };

    const columns = useMemo(() => ([
        {
            name: 'Folio',
            selector: row => row?.folio || 'Sin definir',
            sortable: true,
            wrap: true,
            grow: 1
        },
        {
            name: 'Fecha',
            selector: row => formatDate(row?.fecha),
            sortable: true,
            wrap: true,
            grow: 1
        },
        {
            name: 'Observaciones',
            selector: row => row?.observaciones || '—',
            sortable: true,
            wrap: true,
            grow: 2
        },
        {
            name: 'Status',
            sortable: true,
            width: '140px',
            cell: row => renderBadgeStatus(row?.status)
        },
        {
            name: 'Opciones',
            width: '180px',
            cell: row => (
                <div className="d-flex align-items-center gap-2">
                    <button
                        type="button"
                        className="btn btn-sm btn-link px-2"
                        title="Ver detalle"
                        onClick={() => navigate(`/proyectos/devoluciones/detalle/${row._id}`)}
                    >
                        <i className="fas fa-eye text-primary" />
                    </button>

                    {Number(row?.status) === 1 && (
                        <button
                            type="button"
                            className="btn btn-sm btn-link px-2"
                            title="Editar"
                            onClick={() => navigate(`/proyectos/devoluciones/editar/${row._id}`)}
                        >
                            <i className="fa fa-edit text-warning" />
                        </button>
                    )}

                    {Number(row?.status) !== 2 && (
                        <button
                            type="button"
                            className="btn btn-sm btn-link px-2"
                            title="Eliminar"
                            onClick={() => setOrdenEliminar(row)}
                        >
                            <i className="fas fa-trash text-danger" />
                        </button>
                    )}
                </div>
            )
        }
    ]), [navigate]);

    const paginationOptions = {
        rowsPerPageText: 'Filas por página',
        rangeSeparatorText: 'de',
        selectAllRowsItem: true,
        selectAllRowsItemText: 'Todos'
    };

    return (
        <Fragment>
            <div className="d-flex justify-content-end mb-3">
                <div className="d-flex align-items-center gap-2 w-100 justify-content-end">
                    <div style={{ minWidth: 280 }}>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Buscar en la tabla..."
                            value={textoFiltro}
                            onChange={onChangeFiltroTabla}
                        />
                    </div>

                    <div style={{ minWidth: 200 }}>
                        <Select
                            options={opcionesStatus}
                            value={statusSel}
                            onChange={onChangeStatus}
                            isClearable
                            placeholder="Status..."
                            noOptionsMessage={() => 'Sin resultados'}
                        />
                    </div>

                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => navigate(`/proyectos/devoluciones/nueva/${idProyecto}`)}
                    >
                        <i className="feather icon-plus" /> Agregar devolución
                    </button>
                </div>
            </div>

            <div className="mt-2">
                <DataTable
                    columns={columns}
                    data={filtrado || []}
                    pagination
                    paginationComponentOptions={paginationOptions}
                    highlightOnHover
                    striped
                    responsive
                    persistTableHead
                    noDataComponent="Sin órdenes de devolución para mostrar"
                />
            </div>

            <EliminarOrdenDevolucionModal
                show={!!ordenEliminar}
                idProyecto={idProyecto}
                idOrden={ordenEliminar?._id || null}
                onHide={() => setOrdenEliminar(null)}
            />
        </Fragment>
    );
};

export default OrdenesDevolucionProyecto;
